import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { CreditCard, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "../components/ConfirmDialog";

const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  cartes_bancaires: "CB",
  diners: "Diners",
  discover: "Discover",
  jcb: "JCB",
  unionpay: "UnionPay",
  unknown: "Carte",
};

export default function MyCards() {
  const { role, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [cards, setCards] = useState(null);
  const [defaultPm, setDefaultPm] = useState(null);
  const [removeId, setRemoveId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/my-cards");
      setCards(data.cards || []);
      setDefaultPm(data.defaultPaymentMethodId || null);
    } catch {
      setCards([]);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (role !== "client") { nav("/"); return; }
    load();
  }, [role, authLoading, nav]);

  const remove = (id) => {
    setRemoveId(id);
  };

  const performRemove = async () => {
    const id = removeId;
    setRemoveId(null);
    if (!id) return;
    try {
      await api.delete(`/payments/my-cards/${id}`);
      toast.success("Carte supprimée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (authLoading || cards === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl">
      <p className="overline mb-2">Mon compte</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-ink">Mes moyens de paiement</h1>
      <p className="mt-2 text-subtle">Cartes enregistrées pour vos prochains achats BypassGo.</p>

      <div className="mt-8 flex items-start gap-3 p-4 border border-border bg-surface/60" data-testid="rgpd-notice">
        <ShieldCheck size={16} className="text-[#0A7F2E] mt-0.5 shrink-0" />
        <p className="text-xs text-subtle leading-snug">
          <strong className="text-ink">Aucune donnée de carte n'est stockée chez BypassGo.</strong> Vos cartes sont tokenisées et conservées uniquement par Stripe, conformément au RGPD et au standard PCI-DSS. Vous pouvez supprimer une carte à tout moment, la suppression est immédiate.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {cards.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="no-cards">
            <CreditCard className="mx-auto text-subtle" size={36} />
            <p className="mt-4 text-ink font-medium">Aucune carte enregistrée</p>
            <p className="mt-1 text-xs text-subtle">Cochez "Enregistrer ma carte" lors de votre prochain paiement pour la retrouver ici.</p>
          </div>
        ) : (
          cards.map((c) => (
            <div key={c.id} className="border border-border p-4 flex items-center gap-4" data-testid={`card-row-${c.id}`}>
              <div className="h-10 w-14 bg-ink text-white flex items-center justify-center font-bold text-xs">
                {BRAND_LABEL[c.brand] || c.brand.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-ink">•••• •••• •••• <span className="font-bold">{c.last4}</span></p>
                <p className="text-xs text-subtle mt-0.5">
                  Expire {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                  {defaultPm === c.id && <span className="ml-2 inline-flex items-center gap-1 bg-brand text-white px-1.5 py-0.5 font-bold uppercase tracking-wider text-[9px]">Par défaut</span>}
                </p>
              </div>
              <Button
                onClick={() => remove(c.id)}
                variant="outline"
                size="sm"
                className="border border-border text-destructive hover:border-destructive rounded-md"
                data-testid={`delete-card-${c.id}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="mt-10 flex items-start gap-3 p-4 border border-dashed border-border">
        <AlertTriangle size={16} className="text-subtle mt-0.5 shrink-0" />
        <p className="text-xs text-subtle leading-snug">
          Pour changer la carte par défaut ou ajouter une nouvelle carte, prenez simplement un ticket en cochant "Enregistrer ma carte" — votre nouvelle carte apparaîtra ici.
        </p>
      </div>

      <ConfirmDialog
        open={!!removeId}
        title="Supprimer cette carte ?"
        message="La carte enregistrée sera retirée de votre compte. Vous pourrez en enregistrer une nouvelle lors d'un prochain achat."
        confirmLabel="Supprimer"
        destructive
        onConfirm={performRemove}
        onCancel={() => setRemoveId(null)}
        testid="card-remove-confirm"
      />
    </div>
  );
}
